import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./PspLinkCard.scss";
import { H2, Paragraph } from "components/core/Typography";
import { Image } from "@sparefoot/react-commons";

const TITLE_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 120;

/**
 * Truncates text to a maximum length and adds ellipsis if needed
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum character length
 * @returns {string} Truncated text with ellipsis if needed
 */
const truncateText = (text, maxLength) => {
	if (!text || text.length <= maxLength) {
		return text;
	}
	return `${text.substring(0, maxLength).trim()}...`;
};

/**
 * PspLinkCard component for displaying storage type information
 * @param {Object} props - Component props
 * @param {string} props.image - Image URL
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {string} props.link - Link URL for the card
 * @returns {JSX.Element} PspLinkCard component
 */
export default function PspLinkCard({ image, title, description, link }) {
	const truncatedTitle = truncateText(title, TITLE_MAX_LENGTH);
	const truncatedDescription = truncateText(
		description,
		DESCRIPTION_MAX_LENGTH
	);

	const isTitleTruncated = title && title.length > TITLE_MAX_LENGTH;
	const isDescriptionTruncated =
		description && description.length > DESCRIPTION_MAX_LENGTH;

	return (
		<Link
			to={link}
			className="psp-link-card"
		>
			<div className="psp-link-card-content">
				<div className="psp-link-card-wrapper">
					<div className="psp-link-card-inner">
						<div className="psp-link-card-image">
							<Image
								alt={title}
								src={image}
							/>
						</div>
						<div className="psp-link-card-copy">
							<H2
								className="psp-link-card-title"
								size="large"
								weight="bold"
								{...(isTitleTruncated && { title })}
							>
								{truncatedTitle}
							</H2>
							<Paragraph
								className="psp-link-card-description"
								{...(isDescriptionTruncated && {
									title: description
								})}
							>
								{truncatedDescription}
							</Paragraph>
						</div>
					</div>
				</div>
			</div>
		</Link>
	);
}

PspLinkCard.propTypes = {
	image: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	link: PropTypes.string.isRequired
};
